import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  height: 32,
  padding: '0 10px',
  fontSize: 12,
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 7,
  color: 'var(--text)',
  fontFamily: 'inherit',
  transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
};

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ style, ...rest }, ref) => <input ref={ref} {...rest} style={{ ...FIELD_STYLE, ...style }} />,
);
Input.displayName = 'Input';

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  ({ style, children, ...rest }, ref) => (
    <select ref={ref} {...rest} style={{ ...FIELD_STYLE, paddingRight: 28, ...style }}>
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ style, ...rest }, ref) => (
  <textarea
    ref={ref}
    {...rest}
    style={{
      ...FIELD_STYLE,
      height: 'auto',
      minHeight: 72,
      padding: '8px 10px',
      lineHeight: 1.5,
      resize: 'vertical' as const,
      ...style,
    }}
  />
));
Textarea.displayName = 'Textarea';

export function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
      <label
        htmlFor={htmlFor}
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--text-secondary)',
          letterSpacing: '0.005em',
          textTransform: 'uppercase',
        }}
      >
        {label}
        {hint && (
          <span
            style={{
              color: 'var(--text-dim)',
              fontWeight: 400,
              marginLeft: 6,
              textTransform: 'none',
              letterSpacing: 0,
            }}
          >
            · {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  );
}
